import React from 'react'
import { Button, FormattedHTMLMessage, Input, useNotification } from '@web/walter-ui-kit'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { rpPosthogEvents } from '@walter/shared/constants/posthog'
import { logPosthogEvent } from '@walter/shared/utils/posthog'

import ResendCode from '../ResendCode'
import { lang } from './lang'
import styles from './style.module.css'
import { usePromise } from '~hooks/usePromise'
import { authApi } from '~api'
import { globalLang } from '~globalLang'
import { processRequestErrorData } from '~utils/errors/processRequestErrorData'
import { ActionStatus } from '~components/ResendInput'
import { canPaste, paste } from '~utils/paste'
import { useTraceContext } from '~contexts/TraceContext'
import { getTraceConfig } from '~api/utils'
import { CaptchaPurposes, useCaptcha } from '~hooks/useCaptcha'
import { useRegisteredWithPhone } from '~hooks/useRegisteredWithPhone'

export interface Props {
    onSuccess: () => void
    showTwoFa: boolean
    showVerificationCode: boolean
    resource: string | (() => string)
    initialInputState?: ActionStatus
    isLoading?: boolean
}

// eslint-disable-next-line max-lines-per-function, complexity
function Confirmation({ onSuccess, showVerificationCode, showTwoFa, resource, initialInputState, isLoading }: Props) {
    const showPasteAction = canPaste()
    const intl = useIntl()
    const { push } = useNotification()
    const { operationId } = useTraceContext()
    const { executeCaptcha } = useCaptcha()
    const { isRegisteredWithPhone, phoneNumber, email } = useRegisteredWithPhone()

    const defaultValues = {
        code: '',
        twoFaOrBackupCode: '',
    }

    const {
        control,
        handleSubmit,
        formState: { errors },
        setError,
        setValue,
        watch,
    } = useForm({
        mode: 'onChange',
        defaultValues,
    })

    const { send: onSubmitSend, isLoading: isSubmitting } = usePromise(
        async (data: typeof defaultValues) => {
            const resourceValue = typeof resource === 'function' ? resource() : resource
            const trimmedCode = data.twoFaOrBackupCode.trim()
            const twoFaOrBackupCode = trimmedCode.length > 6 ? { totp_backup: trimmedCode } : { totp_code: trimmedCode }

            await authApi.postPermissions(
                {
                    verification_code: data.code,
                    resource: resourceValue,
                    method: 'post',
                    ...twoFaOrBackupCode,
                },
                getTraceConfig(operationId)
            )
        },
        {
            showErrorNotification: false,
            onSuccess: () => {
                logPosthogEvent(rpPosthogEvents.sendCryptoCodesContinue, {
                    email_code: showVerificationCode,
                    '2fa': showTwoFa,
                    message: 'success',
                })

                onSuccess()
            },
            onError: error => {
                const errorData = processRequestErrorData(error, intl, push)
                let message

                if (errorData.otpInvalid) {
                    message = errorData.otpInvalid

                    setError('code', { message })
                } else if (errorData.twoFaInvalid) {
                    message = errorData.twoFaInvalid

                    setError('twoFaOrBackupCode', { message })
                }

                logPosthogEvent(rpPosthogEvents.sendCryptoCodesContinue, {
                    email_code: showVerificationCode,
                    '2fa': showTwoFa,
                    message,
                })
            },
        }
    )

    const { send } = usePromise(
        async () => {
            const captchaToken = await executeCaptcha(CaptchaPurposes.add_permission)

            await authApi.postVerificationCodes(
                {
                    purpose: 'add_permission',
                    captcha_token: captchaToken,
                },
                getTraceConfig(operationId)
            )
        },
        { toastCaption: globalLang.codeSentNotification }
    )

    const onSubmit = handleSubmit(data => onSubmitSend(data))

    let content

    if (showVerificationCode) {
        const displayValue = isRegisteredWithPhone ? phoneNumber : email ?? 'email'
        const title = isRegisteredWithPhone ? lang.phoneTitle : lang.emailTitle
        const text = isRegisteredWithPhone ? lang.phoneText : lang.emailText
        const label = intl.formatMessage(isRegisteredWithPhone ? lang.phoneLabel : lang.emailLabel)

        const handlePaste = (value: string) => setValue('code', value)

        content = (
            <div className={styles.section}>
                <FormattedHTMLMessage {...title} className={styles.title} tagName="div" />
                <div className={styles.text}>
                    <FormattedHTMLMessage {...text} tagName="div" />
                    <div className={styles.email}>{displayValue}</div>
                </div>

                <Controller
                    rules={{ required: true }}
                    name="code"
                    control={control}
                    render={({ field: { ref, ...field } }) => (
                        <div className={styles.input}>
                            <Input
                                {...field}
                                actionContent={<FormattedHTMLMessage {...lang.paste} />}
                                onAction={() => paste({ setFunction: handlePaste })}
                                showAction={showPasteAction}
                                label={label}
                                inputRef={ref}
                                dataPointer="email_verification_code"
                                isInvalid={!!errors.code}
                                description={errors.code?.message}
                            />
                        </div>
                    )}
                />

                <div className={styles.resend}>
                    <ResendCode onResendCode={send} initialInputState={initialInputState} />
                </div>
            </div>
        )
    }

    if (showTwoFa) {
        const capture2FaEventPosthog = () => logPosthogEvent(rpPosthogEvents.sendCrypto2FAInsert)

        const label = intl.formatMessage(lang.twoFaLabel)

        const handlePaste = (value: string) => setValue('twoFaOrBackupCode', value)

        content = (
            <>
                {content}

                <div className={styles.section}>
                    <FormattedHTMLMessage {...lang.twoFaTitle} className={styles.title} />

                    <Controller
                        rules={{ required: true }}
                        name="twoFaOrBackupCode"
                        control={control}
                        render={({ field: { ref, ...field } }) => (
                            <div className={styles.input}>
                                <Input
                                    {...field}
                                    actionContent={<FormattedHTMLMessage {...lang.paste} />}
                                    onAction={() => paste({ setFunction: handlePaste })}
                                    showAction={showPasteAction}
                                    label={label}
                                    inputRef={ref}
                                    dataPointer="input_two_fa_or_backup"
                                    isInvalid={!!errors.twoFaOrBackupCode}
                                    description={errors.twoFaOrBackupCode?.message}
                                    onBlur={() => {
                                        capture2FaEventPosthog()
                                        field.onBlur()
                                    }}
                                />
                            </div>
                        )}
                    />
                </div>
            </>
        )
    }

    let isButtonDisabled = false
    if (showVerificationCode && !watch('code')) {
        isButtonDisabled = true
    }
    if (showTwoFa && !watch('twoFaOrBackupCode')) {
        isButtonDisabled = true
    }

    return (
        <form onSubmit={onSubmit} className={styles.wrap}>
            {content}
            <div className={styles.button}>
                <Button
                    size="l"
                    fullWidth
                    mode="primary"
                    isLoading={isSubmitting || isLoading}
                    dataPointer="submit_button"
                    isDisabled={isButtonDisabled}
                >
                    <FormattedHTMLMessage {...lang.continue} />
                </Button>
            </div>
        </form>
    )
}

export default Confirmation
